<template>
  <div class="doctor-form">
    <div class="doctor-form__inner">
      <div class="doctor-form__header">
        <h2>
          {{ config.appointmentDoctorTitle || 'Запись на прием к врачу' }}
        </h2>
        <p class="text">
          {{
            config.appointmentDoctorSubtitle ||
            'Выберите услугу и запишитесь в нашу стоматологическую клинику на прием.'
          }}
        </p>
      </div>
      <div class="doctor-form__left">
        <UISelect v-model="officesModel" placeholder="Филиал" :options="affiliatesOptions" />
        <div class="doctor-form__calendar-inner" :class="{ top: availableTimes.length }">
          <ClientOnly>
            <VDatePicker
              v-model.string="datePickerModel"
              class="doctor-form__calendar"
              :min-date="new Date()"
              :disabled-dates="[
                {
                  start: null,
                  end: null,
                  repeat: {
                    on: days => availableDates.includes(days.id),
                  },
                },
              ]"
              :attributes="[
                {
                  content: {
                    style: {
                      fontWeight: 'bold',
                    },
                  },
                  dates: availableDates,
                },
              ]"
              :masks="{ weekdays: 'WW', modelValue: 'YYYY-MM-DD' }"
            />

            <div v-if="availableTimes.length" class="doctor-form__hours">
              <div
                class="doctor-form__hour"
                v-for="i in availableTimes"
                :key="i"
                :class="{ active: selectedTime === i.start }"
                @click="onSelectTime({ start: i.start, end: i.end })"
              >
                {{ dateToTime(i.start) }}
              </div>
            </div>
            <div v-else class="doctor-form__hours">
              <div class="doctor-form__empty-hour">
                На выбранную дату все записи заняты. Пожалуйста, выберите другой день
              </div>
            </div>

            <template #fallback>
              <div class="doctor-form__calendar loading">
                <NuxtIcon class="loader icon--load" name="IconLoading"></NuxtIcon>
              </div>
            </template>
          </ClientOnly>
        </div>
      </div>

      <form
        ref="formRef"
        novalidate
        @change="execute"
        class="doctor-form__right"
        @submit.prevent="onSubmitForm($event)"
      >
        <div class="doctor-form__input">
          <UIInput
            v-model="formModel.full_name"
            class="modal-registration__input-name"
            type="text"
            placeholder="Введите имя"
          />
          <ul v-if="errorFields['full_name']" class="form-block__field-errors">
            <li v-for="error in errorFields['full_name']">
              {{ error.message }}
            </li>
          </ul>
        </div>

        <div class="doctor-form__input">
          <UIInput
            class="modal-registration__input-phone"
            v-model="formModel.mobile_phone"
            type="tel"
            placeholder="Введите номер телефона"
          />
          <ul v-if="errorFields['mobile_phone']" class="form-block__field-errors">
            <li v-for="error in errorFields['mobile_phone']">
              {{ error.message }}
            </li>
          </ul>
        </div>

        <div class="doctor-form__input doctor-form__input--comment">
          <UIInput
            v-model="formModel.comment"
            class="doctor-form__input-comment"
            type="textarea"
            placeholder="Введите комментарий"
          />
          <ul class="form-block__field-errors" v-if="errorFields['comment']">
            <li v-for="error in errorFields['comment']">
              {{ error.message }}
            </li>
          </ul>
        </div>
      </form>

      <div class="doctor-form__bottom">
        <UIButton @click="handleSubmitForm">Записаться</UIButton>

        <div v-if="errorCheckbox" class="form-block__field-errors">
          <div class="form-block__field-error">
            {{ errorCheckbox }}
          </div>
        </div>
        <UICheckbox class="form-block__policy" v-model="checkboxModel">
          Принимаю условия
          <NuxtLink
            :to="{
              name: 'home',
              params: { chapters: useSlugTransform(config.privacyPolicy) },
            }"
            >политики конфиденциальности
          </NuxtLink>
          и даю разрешение на обработку персональных данных в соответствии с
          <NuxtLink
            :to="{
              name: 'home',
              params: { chapters: useSlugTransform(config.consentPersonalData) },
            }"
            >согласием на обработку персональных данных
          </NuxtLink>
          .
        </UICheckbox>
      </div>
    </div>
  </div>

  <UIModal v-model="successModalModel" styled-class="modal-form">
    <div class="form-block">
      <h2 class="text-subheader">Спасибо за запись!</h2>
      <h3>В рабочее время клиники с вами свяжется менеджер для подтверждения записи</h3>
    </div>
  </UIModal>
</template>
<script lang="ts" setup>
import {
  useLazyAsyncData,
  useLazyFetch,
  useSlugTransform,
  ref,
  useState,
  reactive,
  computed,
  watch,
  onUnmounted,
} from '#imports';
import useGoogleRecaptcha from '~/composables/useGoogleRecaptcha';
import type {
  ApiDoctorsSchedulesItem,
  ApiDoctorsSchedulesResponse,
  BlockOffice,
  DoctorsAppointmentCreateBody,
} from '~/types/pages';
import type { SiteSettings } from '~/types/config';
import type { ErrorResponse } from '~/server/utils/serverFetch';
import { useDateFormat } from '@vueuse/core';
import { useAsyncValidator } from '@vueuse/integrations/useAsyncValidator';
import type { Rules } from 'async-validator';
import UIModal from '~/components/UI/modal/UIModal.vue';

interface Props {
  doctorId: number;
}

const props = defineProps<Props>();
const config = useState<SiteSettings>('config');
const affiliates = useState<BlockOffice['offices']>('affiliates');
const datePickerModel = ref(useDateFormat(new Date(), 'YYYY-MM-DD').value);
const officesModel = ref();
const affiliatesOptions = ref([]);
const { executeRecaptcha } = useGoogleRecaptcha();
const errorCheckbox = ref('');
const selectedTime = ref();
const successModalModel = ref(false);

if (affiliates.value) {
  affiliatesOptions.value = affiliates.value.map(el => ({
    value: el.id,
    title: el.title,
  }));
}

// Fetch schedules
const {
  data: schedules,
  pending: schedulesPending,
  error: schedulesError,
} = await useLazyAsyncData<ErrorResponse | ApiDoctorsSchedulesResponse>(
  'getDoctorsSchedules',
  () => {
    return $fetch(`/api/doctors/schedules`, {
      method: 'GET',
    });
  },
);

// Success state
const isSuccess = ref(false);
const serverErrors = ref<Record<string, string[]> | null>(null);

const currentSchedules = computed<ApiDoctorsSchedulesItem[]>(() => {
  if (!schedules.value) {
    return [];
  }

  if (officesModel.value) {
    return (schedules.value as ApiDoctorsSchedulesItem[]).filter(
      el => el.office_id === officesModel.value && el.doctor_id === props.doctorId,
    );
  }

  return (schedules.value as ApiDoctorsSchedulesItem[]).filter(
    el => el.doctor_id === props.doctorId,
  );
});

const dateToTime = (dateString: string) => {
  const date = new Date(dateString);

  const hours = date.getHours();
  const minutes = date.getMinutes();

  const formattedHours = hours < 10 ? `0${hours}` : hours;
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

  return `${formattedHours}:${formattedMinutes}`;
};

const availableDates = computed(() => currentSchedules.value[0]?.cells.map(el => el.date) || []);
const availableTimes = computed(
  () => currentSchedules.value[0]?.cells.find(el => el.date === datePickerModel.value)?.times || [],
);

const onSelectTime = (item: { start: string; end: string }) => {
  selectedTime.value = item.start;
  formModel.dt_end = item.end;
  formModel.dt_start = item.start;
};

const rules = ref<Rules>({
  full_name: {
    required: true,
    type: 'string',
  },
  comment: {
    required: false,
    type: 'string',
  },
  mobile_phone: {
    required: true,
    type: 'string',
  },
});

const formModel = reactive<DoctorsAppointmentCreateBody>({
  doctor_id: props.doctorId,
  office_id: 0,
  dt_start: '0',
  dt_end: '0',
  full_name: '',
  comment: '',
  mobile_phone: '',
});

const checkboxModel = ref(false);

const formRef = ref<HTMLFormElement | null>(null);

const handleSubmitForm = () => {
  if (formRef.value) {
    // Create and dispatch a submit event instead of using the native submit method
    // This ensures that Vue's @submit event handler is triggered
    const submitEvent = new Event('submit', { cancelable: true });
    formRef.value.dispatchEvent(submitEvent);

    // Prevent the default form submission if the event wasn't cancelled
    if (!submitEvent.defaultPrevented) {
      submitEvent.preventDefault();
    }
  }
};

const timeOut = ref<number | undefined>();
// Form submission
const onSubmitForm = async (event: Event) => {
  event.preventDefault();
  const { pass } = await execute();

  serverErrors.value = null;

  if (!checkboxModel.value) {
    errorCheckbox.value = 'Cогласие не принято';
    return;
  }

  if (pass) {
    const formData = new FormData();

    const { token } = await executeRecaptcha('doctorOnlineRegistration');

    for (const item in formModel) {
      if (item === 'comment' && !formModel[item]) {
        formData.append(item, formModel['full_name']);
      } else {
        formData.append(item, formModel[item]);
      }
    }
    if (token) {
      formData.append('g-recaptcha-response', token);
    }

    const { data, error, status } = await useLazyFetch<any>('/api/doctors/appointmentCreate', {
      method: 'POST',
      body: formData,
    });

    if (status.value === 'success') {
      isSuccess.value = true;

      if (data.value.status === 400) {
        serverErrors.value = data.value.data;
      } else {
        successModalModel.value = true;

        timeOut.value = window.setTimeout(() => {
          successModalModel.value = false;
        }, 3000);

        formModel.full_name = '';
        formModel.comment = '';
        formModel.mobile_phone = '';
        formModel.dt_start = '0';
        formModel.dt_end = '0';
        officesModel.value = undefined; // Reset office selection
        checkboxModel.value = false;
        selectedTime.value = undefined;
        errorCheckbox.value = '';
        datePickerModel.value = useDateFormat(new Date(), 'YYYY-MM-DD').value; // Reset date picker to current date
      }
    }
    if (error.value) {
      serverErrors.value = error.value.data.data;
    }
  }
};

const { errorFields, execute } = useAsyncValidator(formModel, rules, {
  validateOption: {
    messages: {
      required: () => 'Обязательное поле',
    },
  },
  immediate: false,
  manual: true,
});

onUnmounted(() => {
  if (timeOut.value) {
    clearTimeout(timeOut.value);
  }
});

watch(
  () => officesModel.value,
  newValue => {
    formModel.office_id = newValue;
  },
  {
    deep: true,
    immediate: false,
  },
);
</script>

<style lang="scss" scoped>
.doctor-form {
  width: 100%;
  padding: 40px 0;
  border-radius: 32px;
  background-color: $color-bg-primary;

  @media (max-width: $media-xl) {
    padding: 40px 16px;
  }

  .loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: 100%;

    .loader {
      transform-origin: center center;
      transition: transform 1s;
      width: 40px;
      height: 40px;
      margin-bottom: 24px;
      animation: rotate 1s infinite;
      display: flex;
      align-items: center;
      font-size: 40px;
    }
  }

  &__hours {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: flex-start;
    justify-content: flex-start;
    max-width: 172px;

    @media (max-width: $media-l) {
      max-width: calc(100% - 124px);
    }
    @media (max-width: $media-s) {
      justify-content: center;
      max-width: unset;
    }
  }

  &__empty-hour {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: $color-typography-primary;
    font-size: 14px;
    line-height: 20px;
    height: 100%;
  }

  &__hour {
    padding: 4px 6px;
    text-align: center;
    width: 52px;
    flex: 0 0 auto;
    font-family: 'Montserrat', sans-serif;
    border: 1px solid $color-theme-secondary;
    border-radius: 16px;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    cursor: pointer;
    @include transition();

    &:hover {
      background-color: $color-theme-secondary;
      color: $color-typography-invert;
    }

    &.active {
      background-color: $color-theme-secondary;
      color: $color-typography-invert;
    }
  }

  &__inner {
    max-width: 960px;
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    margin: 0 auto;
    gap: 32px;

    @media (max-width: $media-xl) {
      gap: 16px;
    }

    @media (max-width: 880px) {
      flex-direction: column;
    }
  }

  &__header {
    width: 100%;
    text-align: center;
    gap: 16px;
  }

  &__left,
  &__right {
    flex: 1 1 calc(50% - 16px);

    @media (max-width: $media-xl) {
      flex: 1 1 auto;
    }
  }

  &__left {
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1 0 calc(50% - 16px);

    @media (max-width: $media-xl) {
      flex: 1 0 472px;
      max-width: 472px;
    }

    @media (max-width: 880px) {
      flex: auto;
      max-width: none;
    }
  }

  &__right {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__input-comment {
    min-height: 128px;

    :deep(textarea) {
      min-height: 128px;
    }
  }

  &__calendar-inner {
    display: flex;
    gap: 8px;
    align-items: stretch;
    justify-content: flex-start;

    &.top {
      align-items: flex-start;

      @media (max-width: $media-s) {
        flex-direction: column;
        align-items: center;
      }
    }
  }

  &__bottom {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 8px;
    max-width: 590px;
    margin: 0 auto;

    :deep(button) {
      width: 100%;
    }
  }
}
</style>

<style lang="scss">
.doctor-form__calendar {
  font-family: $font-family;
  font-size: 14px;
  flex: 1 0 auto;
  max-width: 284px;
  width: 100%;
  height: 294px;

  .vc {
    &-title,
    &-weekday {
      color: $color-typography-primary;
      font-size: 14px;
      font-weight: 600;
    }

    &-day-content {
      opacity: 1 !important;
      color: $color-typography-primary;
      font-weight: 300;
      border: none !important;

      &.vc-disabled {
        color: $color-typography-secondary;
      }

      &:focus {
        box-shadow: none;
      }
    }

    &-day {
      width: 36px;
      height: 36px;
    }

    &-highlight {
      width: 36px;
      height: 36px;
      color: $color-typography-primary !important;
      background-color: $color-bg-highlight-day !important;
    }

    &-highlights ~ .vc-day-content {
      color: $color-typography-primary !important;
    }

    &-arrow {
      &.is-left,
      &.is-right {
        position: relative;

        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 26px;
          height: 26px;
          //background-image: url(~/assets/images/svg/functional/arrow-left.svg);
          background-repeat: no-repeat;
          background-position: center center;
        }

        svg {
          display: none;
        }
      }

      &.is-right {
        &::after {
          left: -26px;
          transform: rotate(180deg);
        }
      }
    }
  }

  .available {
    .vc-day-content {
      font-weight: 600;
    }
  }
}
</style>
